import { Component, DoCheck, Input, OnDestroy } from '@angular/core';
import { FormsModule } from "@angular/forms";
import { debounceTime, Subject } from "rxjs";
import { WebsocketService } from "../../services/websocket.service";
import { NgIf } from "@angular/common";
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-output-card',
  templateUrl: './output-card.component.html',
  styleUrls: ['./output-card.component.scss'],
  standalone: true,
  imports: [TranslatePipe, 
    FormsModule,
    NgIf
  ]
})
export class OutputCardComponent implements OnDestroy, DoCheck {
  @Input() output: any;
  @Input() mac: any;
  @Input() node: any;

  private toggleSubject = new Subject<{ output: number, action: string, node: string }>();
  private toggleSub: any;

  // The switch shows the controller's state — not a copy of it. The copy was only ever
  // refreshed in ngOnInit, which happened to work while trackBy rebuilt the card on every
  // change; the moment the card survived, the switch stopped following reality.
  //
  // `pending` is the optimistic position held while we wait for the controller to confirm.
  private pending: boolean | null = null;
  private pendingTimer: any = null;

  constructor(private websocketService: WebsocketService,
              private router: Router) {
    this.toggleSub = this.toggleSubject.pipe(debounceTime(100)).subscribe(({ output, action }) => {
      this.websocketService.sendMessage({
        type: 'ACTION',
        payload: { mac: this.mac, output, action, node: this.node },
      });      
    });
  }

  get outputChecked(): boolean {
    return this.pending ?? this.output?.state === 'on';
  }

  // The parent mutates the same output object in place, so @Input never changes identity
  // and ngOnChanges never fires — DoCheck is how we notice the controller answered.
  ngDoCheck(): void {
    if (this.pending === null) return;
    if ((this.output?.state === 'on') === this.pending) this.clearPending();
  }

  private clearPending(): void {
    this.pending = null;
    if (this.pendingTimer) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
  }
 
  onToggle(event: Event): void {
    event.stopPropagation();
    const checked = (event.target as HTMLInputElement).checked;
    if (!this.output) return;

    const newState = checked ? 'on' : 'off';
    const outputId = this.output.id;

    this.pending = checked;
    if (this.pendingTimer) clearTimeout(this.pendingTimer);
    // No confirmation in time: stop pretending and show whatever the controller actually says.
    this.pendingTimer = setTimeout(() => this.clearPending(), 2500);

    this.toggleSubject.next({ output: outputId, action: newState, node: this.mac });
  }

  onEdit(): void {
    //console.log(this.output.uuid);
    this.router.navigate(['/output-edit', this.output.eid], { state: { fromTab: 'outputs', controllerMac: this.mac } });
  }

  ngOnDestroy(): void {
    this.toggleSub?.unsubscribe?.();
    this.toggleSubject.complete();
  }
}

